import { X, Copy, Check, Share2 } from 'lucide-react';
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import styles from './ShareCatalogModal.module.css';

interface Props {
  onClose: () => void;
}

// Inline brand SVG icons (16x16 viewBox)
function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0 12 12 0 0011.944 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

export function ShareCatalogModal({ onClose }: Props) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const slug = user?.profileSlug;
  const url = slug ? `${window.location.origin}/profile/${slug}` : null;
  const shareText = url ? t('share.defaultMessage').replace('{url}', url) : '';

  function handleCopy() {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleNativeShare() {
    if (!url || !navigator.share) return;
    navigator.share({ title: 'SiliconVault', text: shareText, url }).catch(() => {});
  }

  function handleFacebookShare() {
    if (!url) return;
    navigator.clipboard.writeText(shareText).then(() => {
      toast(t('share.facebookCopied'), 'success');
    }).catch(() => {});
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>{t('share.title')}</span>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className={styles.body}>
          {slug && url ? (
            <>
              <div className={styles.qrSection}>
                <QRCodeSVG
                  value={url}
                  size={140}
                  bgColor="transparent"
                  fgColor="var(--accent)"
                  level="M"
                />
              </div>

              <div className={styles.urlRow}>
                <input
                  className={styles.urlInput}
                  value={url}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button className={styles.copyBtn} onClick={handleCopy}>
                  {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={2} />}
                  {copied ? t('share.copied') : t('share.copyLink')}
                </button>
              </div>

              <div className={styles.divider} />

              <span className={styles.shareLabel}>{t('share.shareVia')}</span>
              <div className={styles.shareButtons}>
                {typeof navigator.share === 'function' && (
                  <button className={styles.shareBtn} onClick={handleNativeShare}>
                    <span className={styles.shareBtnIcon}><Share2 size={16} strokeWidth={1.75} /></span>
                    {t('share.share')}
                  </button>
                )}
                <a
                  className={styles.shareBtn}
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.shareBtnIcon}><XIcon /></span>
                  X
                </a>
                <button className={styles.shareBtn} onClick={handleFacebookShare}>
                  <span className={styles.shareBtnIcon}><FacebookIcon /></span>
                  Facebook
                </button>
                <a
                  className={styles.shareBtn}
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.shareBtnIcon}><WhatsAppIcon /></span>
                  WhatsApp
                </a>
                <a
                  className={styles.shareBtn}
                  href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className={styles.shareBtnIcon}><TelegramIcon /></span>
                  Telegram
                </a>
              </div>
            </>
          ) : (
            <p className={styles.noSlugMessage}>
              {t('share.noSlug')}{' '}
              <Link to="/settings" className={styles.noSlugLink} onClick={onClose}>
                {t('share.goToSettings')}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
